/**
 * Question-type-specific CSS styles for the survey widget.
 * Includes answer options, emoji, rating, NPS, ranking, matrix, dropdown,
 * and opt-in (mailing) styles. Uses BEM naming with ph- prefix.
 */
export const questionsCSS = `
    /* ===== Answer options list ===== */
    .ph-options {
        max-height: var(--ph-options-max-height, 370px);
        overflow-y: auto;
        width: 100%;
        flex: 1;
        min-height: 0;
    }

    /* ===== Single answer option ===== */
    .ph-option {
        border-radius: 4px;
        display: flex;
        justify-content: start;
        align-items: center;
        color: var(--ph-text-color);
        font-weight: 400;
        font-size: 16px;
        word-break: break-word;
        white-space: pre-line;
        font-family: var(--ph-font-family);
        min-height: 40px;
        box-sizing: border-box;
        width: 100%;
        background-color: var(--ph-text-color-08);
        padding: 12px;
        gap: 10px;
        line-height: normal;
    }

    .ph-option:hover {
        cursor: pointer;
    }

    .ph-option:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
    }

    /* ===== Emoji question ===== */
    .ph-emoji {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 32px;
        padding: 8px 0;
    }

    .ph-emoji__item {
        width: 40px;
        height: 40px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .ph-emoji__item:hover {
        cursor: pointer;
    }

    .ph-emoji__item:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
        border-radius: 4px;
    }

    .ph-emoji__thumb-item {
        display: flex;
        flex-direction: column;
        flex: 1 0 0;
        width: 100%;
        gap: 4px;
    }

    .ph-emoji__thumb-btn {
        width: 100% !important;
        height: auto !important;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;
        flex: 1 0 0;
        border-radius: 4px 2px 2px 4px;
        background-color: #F0F0F1;
    }

    .ph-emoji__thumb-btn--down {
        padding-bottom: 12px;
    }

    .ph-emoji__thumb-btn--up {
        padding-top: 12px;
    }

    .ph-emoji__thumb-text {
        display: flex;
        min-height: 28px;
        height: auto;
        flex-direction: column;
        justify-content: center;
        flex: 1 0 0;
        font-family: 'IBM Plex Sans', sans-serif;
        font-size: 12px;
        font-weight: 600;
        line-height: normal;
        color: var(--ph-text-color);
        white-space: normal;
        word-break: break-word;
    }

    .ph-emoji__thumb-text--left {
        text-align: left;
        align-items: flex-start;
    }

    .ph-emoji__thumb-text--right {
        text-align: right;
        align-items: flex-end;
    }

    /* ===== Rating: context/labels ===== */
    .ph-rating__context {
        display: flex;
        justify-content: space-between;
        align-items: center;
        white-space: pre-line;
        margin-top: 12px;
        gap: 64px;
        width: 100%;
    }

    .ph-rating__label {
        font-family: var(--ph-font-family);
        font-size: 12px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        color: var(--ph-element-color);
    }

    .ph-nps__label {
        font-family: var(--ph-font-family);
        font-size: 14px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
        color: var(--ph-text-color);
    }

    /* ===== Rating: small scale (<=5) ===== */
    .ph-rating--small {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-direction: column;
        padding: 0px;
        width: 100%;
    }

    /* ===== Rating: large scale (>5) ===== */
    .ph-rating--large {
        width: 100%;
        max-height: var(--ph-options-max-height, 370px);
        overflow-y: auto;
        flex: 1;
        min-height: 0;
    }

    /* ===== Rating: element block ===== */
    .ph-rating__elements {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 12px;
        box-sizing: border-box;
        padding: 0px;
        width: 100%;
    }

    /* ===== Rating: single element row ===== */
    .ph-rating__element {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    /* ===== Rating: star button ===== */
    .ph-rating__btn--star {
        padding: 8px 0px;
        background-color: transparent;
        font-weight: 400;
        cursor: pointer;
        border: none;
        border-radius: 4px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        flex: 1;
        box-sizing: border-box;
    }

    .ph-rating__btn--star svg {
        width: var(--ph-star-size, 38px);
        height: var(--ph-star-size, 38px);
        display: block;
    }

    .ph-rating__star-number {
        font-size: 12px;
        font-weight: 600;
        margin-top: 4px;
        font-family: var(--ph-font-family);
    }

    .ph-rating__btn--star:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
        border-radius: 4px;
    }

    /* ===== Rating: number button ===== */
    .ph-rating__btn--number {
        background-color: #F0F0F1;
        font-weight: 600;
        cursor: pointer;
        height: 40px;
        flex: 1;
        color: var(--ph-element-color);
        font-family: var(--ph-font-family);
        align-items: center;
        display: flex;
        justify-content: center;
        font-size: 18px;
        line-height: 16px;
        box-sizing: border-box;
        border: none;
    }

    .ph-rating__btn--number:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
    }

    /* ===== Rating: full-width button ===== */
    .ph-rating__btn--full {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 10px;
        width: 100%;
        font-family: var(--ph-font-family);
        font-size: 16px;
        color: var(--ph-element-color);
        font-weight: 600;
        line-height: normal;
        height: 40px;
        padding: 8px 0px;
        border-radius: 4px;
        border: solid 2px var(--ph-text-color-08);
        cursor: pointer;
    }

    .ph-rating__btn--full:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
    }

    /* ===== NPS number button ===== */
    .ph-rating__btn--nps {
        padding: 2px;
        background-color: transparent;
        font-weight: 700;
        cursor: pointer;
        border: solid 2px var(--ph-element-color);
        margin-left: 0px;
        border-radius: 4px;
        height: 40px;
        width: 32px;
        color: var(--ph-element-color);
        font-family: var(--ph-font-family);
    }

    .ph-rating__btn--nps:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
    }

    /* ===== Ranking ===== */
    .ph-ranking {
        display: flex;
        gap: 12px;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        padding: 0px;
        box-sizing: border-box;
        width: 100%;
    }

    .ph-ranking__item {
        background-color: transparent;
        display: flex;
        justify-content: start;
        align-items: center;
        gap: 8px;
        width: 100%;
    }

    .ph-ranking__number-block {
        position: relative;
        border-radius: 4px;
        padding: 8px;
        background-color: var(--ph-text-color-08);
        border: solid 1px var(--ph-text-color-32);
        width: 47px;
        box-sizing: border-box;
        cursor: pointer;
        height: 40px;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .ph-ranking__number {
        font-weight: 600;
        font-size: 16px;
        color: var(--ph-text-color);
        line-height: normal;
        font-family: var(--ph-font-family);
    }

    .ph-ranking__icon {
        color: var(--ph-element-color);
        cursor: pointer;
        height: 16px;
    }

    /* ===== Ranking dropdown ===== */
    .ph-ranking__dropdown {
        position: absolute;
        top: 100%;
        left: -1px;
        width: calc(100% + 2px);
        z-index: 1;
        display: flex;
        flex-direction: column;
        box-sizing: border-box;
        background-color: var(--ph-bg-color);
    }

    .ph-ranking__dropdown::before {
        content: '';
        position: absolute;
        inset: 0;
        background-color: var(--ph-text-color-08);
        border-left: solid 1px var(--ph-text-color-32);
        border-right: solid 1px var(--ph-text-color-32);
        border-bottom: solid 1px var(--ph-text-color-32);
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        pointer-events: none;
        z-index: -1;
    }

    .ph-ranking__text {
        background-color: var(--ph-text-color-08);
        font-weight: 400;
        line-height: normal;
        border-radius: 4px;
        font-size: 16px;
        color: var(--ph-text-color);
        display: flex;
        justify-content: space-between;
        align-items: center;
        word-break: break-word;
        white-space: pre-line;
        font-family: var(--ph-font-family);
        padding: 8px 12px;
        width: 100%;
        min-height: 40px;
        box-sizing: border-box;
    }

    .ph-ranking__drag-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: grab;
        margin-left: 8px;
        flex-shrink: 0;
    }

    .ph-ranking__item--dragging {
        opacity: 0.5;
    }

    .ph-ranking__item--drag-over {
        background-color: var(--ph-text-color-08);
        border-radius: 4px;
        outline: 2px dashed var(--ph-element-color);
    }

    /* ===== Matrix ===== */
    .ph-matrix {
        max-height: var(--ph-options-max-height, 370px);
        overflow-y: auto;
        flex: 1;
        min-height: 0;
    }
    .ph-matrix__container {
        font-size: 16px;
        color: var(--ph-text-color);
        font-weight: 400;
        line-height: normal;
        text-align: left;
        word-break: break-word;
        font-family: var(--ph-font-family);
        white-space: pre-line;
        width: 100%;
        gap: 12px;
        display: flex;
        flex-direction: column;
        padding: 0;
        box-sizing: border-box;
    }

    .ph-matrix__row {
    }

    .ph-ranking__number-block:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
    }

    /* ===== Dropdown (matrix/ranking) ===== */
    .ph-dropdown__btn {
        box-sizing: border-box;
        background-color: var(--ph-text-color-08);
        color: var(--ph-text-color);
        padding: 8px;
        font-size: 16px;
        border: solid 1px var(--ph-text-color-32);
        cursor: pointer;
        border-radius: 4px;
        width: 100%;
        font-family: var(--ph-font-family);
        word-break: break-word;
        line-height: normal;
        display: flex;
        justify-content: center;
        align-items: start;
        flex-direction: column;
    }

    .ph-dropdown__btn:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
    }

    .ph-dropdown__content {
        width: 100%;
    }

    .ph-dropdown__arrow {
        margin-left: auto;
    }

    .ph-dropdown__answer {
        color: var(--ph-text-color);
        font-size: 14px;
        font-weight: 600;
        line-height: normal;
        word-break: break-word;
        font-family: var(--ph-font-family);
    }

    .ph-dropdown__list {
        display: none;
        z-index: 1;
        width: 100%;
        padding: 10px 0px 0px 12px;
        display: flex;
        flex-direction: column;
        background-color: var(--ph-bg-color);
        gap: 12px;
        justify-content: center;
        align-items: center;
        width: 100%;
        box-sizing: border-box;
    }

    /* ===== Opt-in (mailing question) ===== */
    .ph-optin {
        margin-top: 12px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        font-family: var(--ph-font-family);
    }

    .ph-optin__label {
        display: block;
        color: var(--ph-text-color);
        font-weight: 400;
        font-size: 14px;
        line-height: normal;
        font-family: var(--ph-font-family);
        white-space: pre-line;
    }

    .ph-optin__notice {
        font-size: 12px;
        color: var(--ph-text-color);
        font-weight: 400;
        line-height: normal;
        font-style: italic;
        font-family: var(--ph-font-family);
    }

    /* ===== Process bar (legacy step indicator) ===== */
    .ph-process-bar {
        margin-top: 4px;
        display: flex;
        gap: 5px;
        width: 100%;
    }

    .ph-process-bar__segment {
        border-top: 3px solid var(--ph-text-color-75);
        border-radius: 8px;
        height: 0px;
    }
`;
